import { useParams, useNavigate } from "react-router";
import { useState } from "react";
import { Trash2, Shield, Key, ShieldAlert } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { DetailPage } from "@/archetypes/DetailPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/hooks/use-confirm";
import { toast } from "@/hooks/use-toast";
import { printersApi } from "@/lib/printers";
import { withCurrentValue } from "@/lib/printer-options";
import { EmployeeBranchesTab } from "./EmployeeBranchesTab";

export interface EmployeeDto {
  id: number; firstName: string; lastName: string; fullName: string;
  email: string | null; phone: string | null; mobile: string | null; fax: string | null;
  jobTitle: string | null; defaultDocketPrinterId: number | null; defaultStickerPrinterId: number | null;
  approvalLimitCents: number | null; signature: string | null; mfaEnabled: boolean; deleted: boolean;
  userId: string | null; roles: string[]; identityLockedOut: boolean;
}
interface UserAuditDto { auditRecId: number; eventType: string; eventDescription: string; createdOn: string; ipAddress: string | null; }

const ALL_ROLES = ["Admin", "SalesLead", "Sales", "Production", "Artwork", "Finance", "ReadOnly"];

export function EmployeeDetailPage() {
  const { id = "0" } = useParams();
  const nav = useNavigate(); const confirm = useConfirm();
  const qc = useQueryClient();
  const empQ = useQuery({ queryKey: ["employee", id], queryFn: () => apiFetch<EmployeeDto>(`/api/employees/${id}`) });
  const auditQ = useQuery({ queryKey: ["employee-audit", id],
    queryFn: () => apiFetch<{ items: UserAuditDto[] }>(`/api/user-audit?employeeId=${id}&pageSize=20`),
    enabled: !!empQ.data?.id });

  const updateMut = useMutation({
    mutationFn: (req: Partial<EmployeeDto>) => apiFetch<EmployeeDto>(`/api/employees/${id}`, { method: "PATCH", body: JSON.stringify(req) }),
    onSuccess: () => { toast.success("Saved"); void qc.invalidateQueries({ queryKey: ["employee", id] }); },
  });
  const setRolesMut = useMutation({
    mutationFn: (roles: string[]) => apiFetch(`/api/employees/${id}/roles`, { method: "POST", body: JSON.stringify({ roles }) }),
    onSuccess: () => { toast.success("Roles updated"); void qc.invalidateQueries({ queryKey: ["employee", id] }); },
  });
  const sendResetMut = useMutation({
    mutationFn: () => apiFetch(`/api/employees/${id}/send-password-reset`, { method: "POST" }),
    onSuccess: () => toast.success("Password reset link sent to employee's email"),
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to send reset link"),
  });
  const unlockMut = useMutation({
    mutationFn: () => apiFetch(`/api/employees/${id}/unlock`, { method: "POST" }),
    onSuccess: () => { toast.success("Unlocked"); void qc.invalidateQueries({ queryKey: ["employee", id] }); },
  });
  const deleteMut = useMutation({
    mutationFn: () => apiFetch(`/api/employees/${id}`, { method: "DELETE" }),
    onSuccess: () => { toast.success("Deleted"); void nav("/admin/employees"); },
  });

  if (!empQ.data) return null;
  const e = empQ.data;

  async function handleDelete() {
    const deps = await apiFetch<{ openQuotes: number; openJobs: number; isBranchManager: boolean }>(`/api/employees/${id}/dependencies`);
    const body = (
      <div className="space-y-4">
        <p>
          Are you sure you want to delete this employee? This action will remove all their information and revoke their access immediately.
        </p>
        {deps.isBranchManager ? (
          <p className="font-medium text-destructive">
            Warning: This employee manages a branch. You should reassign this in SA-5 before proceeding.
          </p>
        ) : (
          <p className="text-sm text-slate-500">
            They currently have {deps.openQuotes} open quotes and {deps.openJobs} open jobs.
          </p>
        )}
      </div>
    );
    if (await confirm({ title: "Delete Employee", body, intent: "destructive", confirmLabel: "Delete Employee" })) {
      deleteMut.mutate();
    }
  }

  return (
    <DetailPage
      title={`${e.fullName}${e.deleted ? " (deleted)" : ""}`}
      tabActions={
        <Button variant="destructive" onClick={() => { void handleDelete(); }}>
          <Trash2 className="mr-1.5 h-4 w-4" /> Delete
        </Button>
      }
      tabs={[
        { id: "profile", label: "Profile", content: (
          <ProfileForm employee={e} onSubmit={(req) => updateMut.mutate(req)} pending={updateMut.isPending} />
        )},
        { id: "identity", label: "Identity & Access", content: (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="bg-white border rounded-xl shadow-sm">
                <div className="px-5 py-3 border-b">
                  <h3 className="text-base font-semibold flex items-center gap-2 text-slate-800">
                    <Shield className="h-4 w-4 text-primary" /> Security Settings
                  </h3>
                </div>
                <div className="p-5 space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-800 flex items-center gap-2"><Key className="h-4 w-4 text-slate-500" /> Password Reset</div>
                      <div className="text-sm text-slate-500 mt-0.5">Send a secure link to reset their password.</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => sendResetMut.mutate()} disabled={sendResetMut.isPending}>
                      {sendResetMut.isPending ? "Sending..." : "Send Link"}
                    </Button>
                  </div>
                  
                  {e.identityLockedOut && (
                    <div className="flex items-center justify-between pt-6 border-t">
                      <div>
                        <div className="font-medium text-destructive flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Account Locked</div>
                        <div className="text-sm text-slate-500 mt-0.5">User exceeded maximum login attempts.</div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => unlockMut.mutate()}>Unlock Account</Button>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-6 border-t">
                    <div>
                      <div className="font-medium text-slate-800">Multi-Factor Auth (MFA)</div>
                      <div className="text-sm text-slate-500 mt-0.5">Required for all administrative actions.</div>
                    </div>
                    {e.mfaEnabled ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">Enabled</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">Not Enrolled</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-white border rounded-xl shadow-sm">
                <div className="px-5 py-3 border-b">
                  <h3 className="text-base font-semibold text-slate-800">Assigned Roles</h3>
                </div>
                <div className="p-5">
                  <RolesEditor current={e.roles} onSave={(r) => setRolesMut.mutate(r)} pending={setRolesMut.isPending} />
                </div>
              </div>
            </div>
          </div>
        )},
        { id: "defaults", label: "Defaults", content: (
          <DefaultsForm employee={e} onSubmit={(req) => updateMut.mutate(req)} pending={updateMut.isPending} />
        )},
        { id: "branches", label: "Branches", content: (
          <EmployeeBranchesTab employeeId={e.id} hasUserId={e.userId != null} />
        )},
        { id: "activity", label: "Activity", content: (
          <ul className="space-y-2 text-sm">
            {(auditQ.data?.items ?? []).map((a) => (
              <li key={a.auditRecId}><strong>{a.eventType}</strong> {a.eventDescription} <span className="text-muted-foreground">({a.createdOn})</span></li>
            ))}
            {(auditQ.data?.items ?? []).length === 0 && <li className="text-muted-foreground">No recent events.</li>}
          </ul>
        )},
      ]}
    />
  );
}

function ProfileForm({ employee, onSubmit, pending }: { employee: EmployeeDto; onSubmit: (r: Partial<EmployeeDto>) => void; pending: boolean }) {
  const [s, setS] = useState({ firstName: employee.firstName, lastName: employee.lastName,
    email: employee.email ?? "", phone: employee.phone ?? "", mobile: employee.mobile ?? "",
    fax: employee.fax ?? "", jobTitle: employee.jobTitle ?? "", signature: employee.signature ?? "" });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...employee, ...s }); }} className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div><Label htmlFor="fn">First name</Label><Input id="fn" value={s.firstName} onChange={(e) => setS({ ...s, firstName: e.target.value })} required /></div>
      <div><Label htmlFor="ln">Last name</Label><Input id="ln" value={s.lastName} onChange={(e) => setS({ ...s, lastName: e.target.value })} required /></div>
      <div><Label htmlFor="em">Email</Label><Input id="em" type="email" value={s.email} onChange={(e) => setS({ ...s, email: e.target.value })} required /></div>
      <div><Label htmlFor="ph">Phone</Label><Input id="ph" value={s.phone} onChange={(e) => setS({ ...s, phone: e.target.value })} /></div>
      <div><Label htmlFor="mo">Mobile</Label><Input id="mo" value={s.mobile} onChange={(e) => setS({ ...s, mobile: e.target.value })} /></div>
      <div><Label htmlFor="ti">Job title</Label><Input id="ti" value={s.jobTitle} onChange={(e) => setS({ ...s, jobTitle: e.target.value })} /></div>
      <div className="md:col-span-2"><Label htmlFor="sig">Signature (HTML)</Label><Input id="sig" value={s.signature} onChange={(e) => setS({ ...s, signature: e.target.value })} /></div>
      <div className="md:col-span-2"><Button type="submit" disabled={pending}>Save profile</Button></div>
    </form>
  );
}

function RolesEditor({ current, onSave, pending }: { current: string[]; onSave: (roles: string[]) => void; pending?: boolean }) {
  const [sel, setSel] = useState(new Set(current));
  
  const isChanged = current.length !== sel.size || current.some(r => !sel.has(r));

  return (
    <div className="space-y-6">
      <div className="rounded-lg border divide-y overflow-hidden">
        {ALL_ROLES.map((r) => (
          <div key={r} className="flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition-colors">
            <Label htmlFor={`role-${r}`} className="flex-1 cursor-pointer font-medium text-slate-700">{r}</Label>
            <Switch 
              id={`role-${r}`}
              checked={sel.has(r)} 
              onCheckedChange={(checked) => {
                const next = new Set(sel);
                if (checked) next.add(r); else next.delete(r);
                setSel(next);
              }} 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <Button onClick={() => onSave([...sel])} disabled={!isChanged || pending}>
          {pending ? "Saving..." : "Save role changes"}
        </Button>
      </div>
    </div>
  );
}

export function DefaultsForm({ employee, onSubmit, pending }: { employee: EmployeeDto; onSubmit: (r: Partial<EmployeeDto>) => void; pending: boolean }) {
  const [s, setS] = useState({
    defaultDocketPrinterId: employee.defaultDocketPrinterId ?? null,
    defaultStickerPrinterId: employee.defaultStickerPrinterId ?? null,
    approvalLimitCents: employee.approvalLimitCents ?? null,
  });
  const printersQ = useQuery({ queryKey: ["printers"], queryFn: () => printersApi.list() });
  const printers = printersQ.data ?? [];
  const docketOptions = withCurrentValue(
    printers.filter((p) => p.standardA4Printer),
    printersQ,
    s.defaultDocketPrinterId,
    "not an A4 printer",
  );
  const stickerOptions = withCurrentValue(
    printers.filter((p) => p.labelPrinter),
    printersQ,
    s.defaultStickerPrinterId,
    "not a label printer",
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...employee, ...s }); }} className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div>
        <Label htmlFor="dp">Default docket printer</Label>
        {/* Native select, not shadcn's Select: Radix renders its listbox in a portal with no
            real <option> elements, which would make it impossible to assert the selected value
            the way the tests here need to. */}
        <select
          id="dp"
          className="flex h-9 w-full items-center rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={s.defaultDocketPrinterId ?? ""}
          onChange={(e) => setS({ ...s, defaultDocketPrinterId: e.target.value ? Number(e.target.value) : null })}
          disabled={printersQ.isLoading}
        >
          <option value="">— None —</option>
          {docketOptions.map((p) => (
            <option key={p.printerId} value={p.printerId}>{p.printerName}</option>
          ))}
        </select>
      </div>
      <div>
        <Label htmlFor="sp">Default sticker printer</Label>
        <select
          id="sp"
          className="flex h-9 w-full items-center rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={s.defaultStickerPrinterId ?? ""}
          onChange={(e) => setS({ ...s, defaultStickerPrinterId: e.target.value ? Number(e.target.value) : null })}
          disabled={printersQ.isLoading}
        >
          <option value="">— None —</option>
          {stickerOptions.map((p) => (
            <option key={p.printerId} value={p.printerId}>{p.printerName}</option>
          ))}
        </select>
      </div>
      <div><Label htmlFor="al">Approval limit (cents)</Label><Input id="al" type="number" value={s.approvalLimitCents ?? ""} onChange={(e) => setS({ ...s, approvalLimitCents: e.target.value ? Number(e.target.value) : null })} /></div>
      <div className="md:col-span-2"><Button type="submit" disabled={pending}>Save defaults</Button></div>
    </form>
  );
}
